import React, { useState } from 'react';
import {
  InterviewNodeData,
  IndividualConfig,
  CodingConfig,
  GDConfig,
  getInterviewTypeMeta,
} from '../../types/interviewBuilder';
import './ConfigPanel.css';

// Helper to render interview type icons as SVGs (matching interview widget type-options)
const InterviewTypeIcon: React.FC<{ icon: string; color: string; size?: number }> = ({ icon, color, size = 20 }) => {
  switch (icon) {
    case 'triangle': // Mock Interview - Robot/screen icon
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 8V4H8" />
          <rect x="4" y="8" width="16" height="12" rx="2" />
          <circle cx="9" cy="13" r="1" fill={color} />
          <circle cx="15" cy="13" r="1" fill={color} />
          <path d="M9 17h6" />
        </svg>
      );
    case 'square': // Coding - Code brackets icon
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="16 18 22 12 16 6" />
          <polyline points="8 6 2 12 8 18" />
          <line x1="14" y1="4" x2="10" y2="20" />
        </svg>
      );
    case 'diamond': // Group Discussion - People icon
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    default:
      return null;
  }
};

interface ConfigPanelProps {
  nodeData: InterviewNodeData | null;
  roundName: string;
  scheduledDate: string;
  isDateLocked: boolean;
  isDateSyncEnabled: boolean;
  dateWarning: 'conflict' | 'past' | null;
  dateWarningClosing: boolean;
  onDateSyncToggle: () => void;
  isOpen: boolean;
  onToggle: () => void;
  onRenameRound: (name: string) => void;
  onScheduledDateChange: (date: string) => void;
  onUpdateConfig: (config: IndividualConfig | CodingConfig | GDConfig) => void;
  onClose: () => void;
  onMarkConfigured: () => void;
  // JD Mode props
  isJDMode?: boolean;
  jdText?: string;
  jdWordCount?: number;
  isJDValid?: boolean;
  isJDInvalid?: boolean;
  onJDTextChange?: (text: string) => void;
  isJDLocked?: boolean;
  onProcessJD?: () => void;
  isJDProcessing?: boolean;
  isJDProcessed?: boolean;
  jdProcessingFailed?: boolean;
}

const ConfigPanel: React.FC<ConfigPanelProps> = ({
  nodeData,
  roundName,
  scheduledDate,
  isDateLocked,
  isDateSyncEnabled,
  dateWarning,
  dateWarningClosing,
  onDateSyncToggle,
  isOpen,
  onToggle,
  onRenameRound,
  onScheduledDateChange,
  onUpdateConfig,
  onClose,
  onMarkConfigured,
  isJDMode = false,
  jdText = '',
  jdWordCount = 0,
  isJDValid = false,
  isJDInvalid = false,
  onJDTextChange,
  isJDLocked = false,
  onProcessJD,
  isJDProcessing = false,
  isJDProcessed = false,
  jdProcessingFailed = false,
}) => {
  const meta = nodeData ? getInterviewTypeMeta(nodeData.interviewType) : null;

  const renderIndividualConfig = () => {
    if (!nodeData) return null;
    const config = nodeData.config as IndividualConfig;
    return (
      <>
        {/* Difficulty */}
        <div className="config-panel__field">
          <label className="config-panel__label">Difficulty</label>
          <div className="config-panel__select-group">
            {(['easy', 'medium', 'hard'] as const).map((option) => (
              <button
                key={option}
                className={`config-panel__option ${config.difficulty === option ? 'config-panel__option--active' : ''}`}
                onClick={() => onUpdateConfig({ ...config, difficulty: option })}
              >
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Experience Focus */}
        <div className="config-panel__field">
          <label className="config-panel__label">Experience Focus</label>
          <div className="config-panel__select-group">
            {([
              { value: 'varied', label: 'Varied' },
              { value: 'recent_deep', label: 'Recent & Deep' },
            ] as const).map((option) => (
              <button
                key={option.value}
                className={`config-panel__option ${config.experience_focus === option.value ? 'config-panel__option--active' : ''}`}
                onClick={() => onUpdateConfig({ ...config, experience_focus: option.value })}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Mode */}
        <div className="config-panel__field">
          <label className="config-panel__label">Interview Mode</label>
          <div className="config-panel__select-group config-panel__select-group--vertical">
            {([
              { value: 'technical', label: 'Technical', desc: 'Focus on technical skills' },
              { value: 'behavioral', label: 'Behavioral', desc: 'Focus on soft skills' },
              { value: 'professional', label: 'Professional', desc: 'Mixed approach' },
            ] as const).map((option) => (
              <button
                key={option.value}
                className={`config-panel__option config-panel__option--large ${config.mode === option.value ? 'config-panel__option--active' : ''}`}
                onClick={() => onUpdateConfig({ ...config, mode: option.value })}
              >
                <span className="config-panel__option-title">{option.label}</span>
                <span className="config-panel__option-desc">{option.desc}</span>
              </button>
            ))}
          </div>
        </div>
      </>
    );
  };

  const renderCodingConfig = () => {
    if (!nodeData) return null;
    const config = nodeData.config as CodingConfig;
    return (
      <>
        {/* Language */}
        <div className="config-panel__field">
          <label className="config-panel__label">Programming Language</label>
          <select
            className="config-panel__select"
            value={config.language}
            onChange={(e) => onUpdateConfig({ ...config, language: e.target.value })}
          >
            <option value="python">Python</option>
            <option value="javascript">JavaScript</option>
            <option value="typescript">TypeScript</option>
            <option value="java">Java</option>
            <option value="kotlin">Kotlin</option>
            <option value="cpp">C++</option>
            <option value="c">C</option>
            <option value="csharp.net">C#</option>
            <option value="fsharp.net">F#</option>
            <option value="go">Go</option>
            <option value="rust">Rust</option>
            <option value="ruby">Ruby</option>
            <option value="php">PHP</option>
            <option value="sqlite3">SQL (SQLite)</option>
            <option value="rscript">R</option>
            <option value="d">D</option>
            <option value="fortran">Fortran</option>
            <option value="basic.net">Basic.NET</option>
            <option value="fsi">F# Interactive</option>
          </select>
        </div>

        {/* Difficulty */}
        <div className="config-panel__field">
          <label className="config-panel__label">Difficulty</label>
          <div className="config-panel__select-group">
            {(['easy', 'medium', 'hard'] as const).map((option) => (
              <button
                key={option}
                className={`config-panel__option ${config.difficulty === option ? 'config-panel__option--active' : ''}`}
                onClick={() => onUpdateConfig({ ...config, difficulty: option })}
              >
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Category */}
        <div className="config-panel__field">
          <label className="config-panel__label">Category (optional)</label>
          <select
            className="config-panel__select"
            value={config.category}
            onChange={(e) => onUpdateConfig({ ...config, category: e.target.value })}
          >
            <option value="">Any Category</option>
            <option value="arrays">Arrays</option>
            <option value="strings">Strings</option>
            <option value="linked_lists">Linked Lists</option>
            <option value="trees">Trees</option>
            <option value="graphs">Graphs</option>
            <option value="dynamic_programming">Dynamic Programming</option>
            <option value="sorting">Sorting</option>
          </select>
        </div>

        {/* Mode */}
        <div className="config-panel__field">
          <label className="config-panel__label">Session Mode</label>
          <div className="config-panel__select-group">
            {([
              { value: 'practice', label: 'Practice' },
              { value: 'timed', label: 'Timed' },
            ] as const).map((option) => (
              <button
                key={option.value}
                className={`config-panel__option ${config.mode === option.value ? 'config-panel__option--active' : ''}`}
                onClick={() => onUpdateConfig({ ...config, mode: option.value })}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Duration (only for timed mode) */}
        {config.mode === 'timed' && (
          <div className="config-panel__field">
            <label className="config-panel__label">Duration</label>
            <div className="config-panel__fixed-value">30 minutes</div>
          </div>
        )}
      </>
    );
  };

  const renderGDConfig = () => {
    if (!nodeData) return null;
    const config = nodeData.config as GDConfig;
    return (
      <>
        {/* Difficulty */}
        <div className="config-panel__field">
          <label className="config-panel__label">Difficulty Level</label>
          <div className="config-panel__select-group">
            {(['beginner', 'intermediate', 'advanced'] as const).map((option) => (
              <button
                key={option}
                className={`config-panel__option ${config.difficulty === option ? 'config-panel__option--active' : ''}`}
                onClick={() => onUpdateConfig({ ...config, difficulty: option })}
              >
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Category */}
        <div className="config-panel__field">
          <label className="config-panel__label">Topic Category</label>
          <select
            className="config-panel__select"
            value={config.category}
            onChange={(e) => onUpdateConfig({ ...config, category: e.target.value })}
          >
            <option value="">Any Category</option>
            <option value="technology">Technology</option>
            <option value="business">Business</option>
            <option value="social">Social Issues</option>
            <option value="current_affairs">Current Affairs</option>
            <option value="abstract">Abstract Topics</option>
          </select>
        </div>

        {/* Topic Preview */}
        <div className="config-panel__field">
          <label className="config-panel__label">Topic Selection</label>
          <div className="config-panel__info-box">
            <svg className="config-panel__info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
            <span>A topic will be randomly selected based on your preferences when the interview starts.</span>
          </div>
        </div>
      </>
    );
  };

  const renderConfigFields = () => {
    if (!nodeData) return null;
    switch (nodeData.interviewType) {
      case 'mock_ai':
        return renderIndividualConfig();
      case 'coding':
        return renderCodingConfig();
      case 'group_discussion':
        return renderGDConfig();
      default:
        return null;
    }
  };

  const renderJDPanelContent = () => {
    return (
      <>
        {/* Header */}
        <div className="config-panel__header config-panel__header--jd">
          <div className="config-panel__header-info">
            <span className="config-panel__header-icon config-panel__header-icon--jd">
              <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="#a3e635" strokeWidth="1.5">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                <line x1="9" y1="12" x2="15" y2="12" />
                <line x1="9" y1="16" x2="15" y2="16" />
              </svg>
            </span>
            <div>
              <h3 className="config-panel__title">Job Description</h3>
              <p className="config-panel__subtitle">Paste the JD to tailor the interview</p>
            </div>
          </div>
          <button className="config-panel__close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* JD Content */}
        <div className="config-panel__content config-panel__content--jd">
          <div className="config-panel__field config-panel__jd-field">
            <label className="config-panel__label">
              Job Description Text
              <span className={`config-panel__word-badge ${isJDValid ? 'config-panel__word-badge--valid' : ''} ${isJDInvalid ? 'config-panel__word-badge--invalid' : ''}`}>
                {jdWordCount} / 10-650 words
              </span>
            </label>
            <textarea
              className={`config-panel__textarea ${isJDInvalid ? 'config-panel__textarea--error' : ''} ${isJDLocked ? 'config-panel__textarea--locked' : ''}`}
              placeholder={`Paste job description here...

Job Title
-------------------------

Job Purpose
-------------------------

Key Responsibilities
-------------------------

Qualifications
-------------------------`}
              value={jdText}
              onChange={(e) => !isJDLocked && onJDTextChange?.(e.target.value)}
              readOnly={isJDLocked}
            />
            {isJDLocked && (
              <div className="config-panel__jd-locked-notice">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <span>JD is locked after extraction. Create a new interview for a different JD.</span>
              </div>
            )}
            {isJDInvalid && jdWordCount < 10 && jdWordCount > 0 && (
              <span className="config-panel__field-error">Too short - minimum 10 words required</span>
            )}
            {isJDInvalid && jdWordCount > 650 && (
              <span className="config-panel__field-error">Too long - maximum 650 words allowed</span>
            )}
          </div>

          <div className="config-panel__jd-tip">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
            <span>A detailed job description helps generate more relevant interview questions tailored to the role.</span>
          </div>
        </div>

        {/* Footer */}
        <div className="config-panel__footer config-panel__footer--jd">
          <div
            className={`config-panel__jd-status ${
              isJDProcessed ? 'config-panel__jd-status--valid' : isJDValid ? 'config-panel__jd-status--ready' : ''
            } ${jdProcessingFailed ? 'config-panel__jd-status--error' : ''}`}
          >
            {isJDProcessed ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span>JD processed — add interview rounds</span>
              </>
            ) : isJDProcessing ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
                <span>Extracting skills and requirements from your JD...</span>
              </>
            ) : jdProcessingFailed ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                <span>Extraction failed — fix the JD and try again</span>
              </>
            ) : isJDValid ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4M12 8h.01" />
                </svg>
                <span>JD ready — click Process JD to extract skills</span>
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4M12 8h.01" />
                </svg>
                <span>{jdWordCount === 0 ? 'Paste a job description to continue' : 'Adjust word count (10-650 words)'}</span>
              </>
            )}
          </div>
          {!isJDLocked && (
            <button
              type="button"
              className="config-panel__process-jd-btn"
              onClick={onProcessJD}
              disabled={!isJDValid || isJDProcessing || isJDProcessed}
            >
              {isJDProcessing ? (
                <>
                  <span className="config-panel__process-jd-spinner" aria-hidden="true" />
                  Processing...
                </>
              ) : isJDProcessed ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  JD Processed
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 0 C13.5 7 17 10.5 24 12 C17 13.5 13.5 17 12 24 C10.5 17 7 13.5 0 12 C7 10.5 10.5 7 12 0Z" fill="currentColor" stroke="none" />
                  </svg>
                  {jdProcessingFailed ? 'Retry Process JD' : 'Process JD'}
                </>
              )}
            </button>
          )}
        </div>
      </>
    );
  };

  const renderPanelContent = () => {
    // JD Mode - show JD configuration
    if (isJDMode) {
      return renderJDPanelContent();
    }

    if (!nodeData || !meta) {
      return (
        <div className="config-panel__empty-state">
          <svg className="config-panel__empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
          </svg>
          <h3>Select a Node</h3>
          <p>Click on an interview node to configure its settings</p>
        </div>
      );
    }

    return (
      <>
        {/* Header */}
        <div className="config-panel__header">
          <div className="config-panel__header-info">
            <span className="config-panel__header-icon">
              <InterviewTypeIcon icon={meta.icon} color={meta.color} size={22} />
            </span>
            <div>
              <h3 className="config-panel__title">{meta.title}</h3>
              <p className="config-panel__subtitle">Configure settings</p>
            </div>
          </div>
          <button className="config-panel__close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Configuration Fields */}
        <div className="config-panel__content">
          {/* Round Name */}
          <div className="config-panel__field">
            <label className="config-panel__label">Round Name</label>
            <input
              type="text"
              className="config-panel__input"
              value={roundName}
              onChange={(e) => onRenameRound(e.target.value)}
              placeholder="e.g., Technical Round"
              maxLength={30}
            />
          </div>

          {/* Scheduled Date/Time */}
          <div className="config-panel__field">
            <div className="config-panel__label-row">
              <label className="config-panel__label">Scheduled Date & Time</label>
              {isDateLocked && (
                <span className="config-panel__locked-badge">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                  </svg>
                  Synced
                </span>
              )}
            </div>
            <input
              type="datetime-local"
              className={`config-panel__input config-panel__datetime ${isDateLocked ? 'config-panel__datetime--locked' : ''} ${dateWarning ? 'config-panel__datetime--error' : ''}`}
              value={scheduledDate}
              onChange={(e) => onScheduledDateChange(e.target.value)}
              disabled={isDateLocked}
            />
          </div>

          {/* Date Sync Toggle */}
          <div className="config-panel__field config-panel__sync-field">
            <div className="config-panel__sync-row">
              <span className="config-panel__sync-text">Sync dates with 1 hour slots</span>
              <button 
                className={`config-panel__toggle ${isDateSyncEnabled ? 'config-panel__toggle--active' : ''}`}
                onClick={onDateSyncToggle}
                role="switch"
                aria-checked={isDateSyncEnabled}
              >
                <span className="config-panel__toggle-track">
                  <span className="config-panel__toggle-thumb" />
                </span>
              </button>
            </div>
            {isDateSyncEnabled && (
              <p className="config-panel__sync-hint">
                All rounds are synced 1 hour apart. New rounds will auto-schedule. Toggle off to edit individually.
              </p>
            )}
          </div>

          {renderConfigFields()}
        </div>

        {/* Footer Actions */}
        <div className="config-panel__footer">
          {dateWarning && (
            <div className={`config-panel__date-error ${dateWarningClosing ? 'config-panel__date-error--closing' : ''}`}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {dateWarning === 'past' 
                ? 'Scheduled time must be at least 1 hour from now' 
                : 'Two rounds cannot have the same date and time'}
            </div>
          )}
          <button 
            className="config-panel__save-btn"
            onClick={() => {
              onMarkConfigured();
              onClose();
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 13l4 4L19 7" />
            </svg>
            Save Round
          </button>
        </div>
      </>
    );
  };

  // Don't render the panel at all if there's no node data, not in JD mode, and panel is closed
  if (!nodeData && !isJDMode && !isOpen) {
    return null;
  }

  return (
    <div className={`config-panel ${!isOpen ? 'config-panel--collapsed' : ''} ${isJDMode ? 'config-panel--jd' : ''}`}>
      {/* Panel Body - Slides in/out */}
      <div className="config-panel__body">
        {renderPanelContent()}
      </div>
    </div>
  );
};

export default ConfigPanel;
